/** @jsxImportSource preact */
import { useState, useEffect } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'
import { PreviousDefaultWaiver } from './PreviousDefaultWaiver'
import { FraudSettings } from './FraudSettings'
import { FraudOperators } from './FraudOperators'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

interface FulfilmentItem {
  workflowId: string
  manualFraudCheckReason?: string
}

type FraudAction = 'approve' | 'decline'

const TABS = [
  ['booking', 'Individual check'],
  ['settings', 'Eligibility rules'],
  ['operators', 'Approved agents'],
] as const

type TabValue = (typeof TABS)[number][0]

export function Fraud() {
  const [bookingId, setBookingId] = useState('')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  const [fulfilments, setFulfilments] = useState<FulfilmentItem[]>([])
  const [selectedFulfilmentIndex, setSelectedFulfilmentIndex] = useState<number>(-1)
  const [fetchingFulfilments, setFetchingFulfilments] = useState(false)
  const [action, setAction] = useState<FraudAction>('approve')
  const [note, setNote] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Two jobs on one screen: finalising the booking in front of you, and the system-wide switches
  // that decide what happens to everyone else. They were interleaved, which made the review
  // harder to read and the switches look like part of it.
  const [tab, setTab] = useState<TabValue>(() => {
    if (typeof window === 'undefined') return 'booking'
    const requested = new URLSearchParams(window.location.search).get('tab')
    return requested === 'settings' || requested === 'operators' ? requested : 'booking'
  })

  // The tab is read from the URL on load, so it has to be written back on change — otherwise a
  // reload drops the analyst somewhere other than where they were.
  const selectTab = (next: TabValue) => {
    setTab(next)
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (next === 'booking') url.searchParams.delete('tab')
    else url.searchParams.set('tab', next)
    window.history.replaceState({}, '', url)
  }

  // Arrow keys move between tabs, which is what the W3C tabs pattern expects once these carry
  // tab roles — without it the roles promise behaviour the buttons do not have.
  const onTabKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    event.preventDefault()
    const index = TABS.findIndex(([value]) => value === tab)
    const next = TABS[(index + (event.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length][0]
    selectTab(next)
    document.getElementById(`fraud-tab-${next}`)?.focus()
  }

  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  useEffect(() => {
    if (bookingId.trim()) {
      const timeoutId = setTimeout(() => {
        fetchFulfilmentsByBookingId(bookingId.trim())
      }, 800)
      return () => clearTimeout(timeoutId)
    } else {
      setFulfilments([])
      setSelectedFulfilmentIndex(-1)
    }
  }, [bookingId])

  const fetchFulfilmentsByBookingId = async (id: string) => {
    if (!id) return

    setFetchingFulfilments(true)
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${id}/fulfilments`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch fulfilments')
      }

      const data: FulfilmentItem[] = await response.json()
      setFulfilments(data)

      if (data.length === 0) {
        setErrorMessage('No fulfilments found for this booking ID')
      }
    } catch (error) {
      console.error('Error fetching fulfilments:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch fulfilments')
      setFulfilments([])
    } finally {
      setFetchingFulfilments(false)
    }
  }

  const handleRefreshFulfilments = () => {
    if (bookingId.trim()) {
      fetchFulfilmentsByBookingId(bookingId.trim())
    }
  }

  const handleFulfilmentSelectionChange = (index: number) => {
    setSelectedFulfilmentIndex(index)
  }

  const resetForm = () => {
    setFormStatus('idle')
    setErrorMessage('')
    setAction('approve')
    setShowConfirmation(false)
    setSelectedFulfilmentIndex(-1)
    setNote('')
  }

  const handleFormSubmit = async (e: Event) => {
    e.preventDefault()

    if (!bookingId.trim()) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    if (!note.trim()) {
      setErrorMessage('Note is required')
      setFormStatus('error')
      return
    }

    if (selectedFulfilmentIndex === -1 || !fulfilments[selectedFulfilmentIndex]) {
      setErrorMessage('Please select a fulfilment from the table')
      setFormStatus('error')
      return
    }

    const selectedFulfilment = fulfilments[selectedFulfilmentIndex]

    if (!selectedFulfilment.workflowId) {
      setErrorMessage('Selected fulfilment does not have a workflow ID')
      setFormStatus('error')
      return
    }

    if (!showConfirmation) {
      setShowConfirmation(true)
      return
    }

    setFormStatus('loading')
    setErrorMessage('')

    try {
      const username = user && (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email)
      const submittedNote = username ? `${note.trim()} - submitted by ${username}` : note.trim()

      const endpoint = `${config.apiBaseUrl}/bookings/fraud/finalise`
      const requestBody = {
        workflowId: selectedFulfilment.workflowId,
        passed: action === 'approve',
        note: submittedNote,
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to finalise the review')
      }

      setFormStatus('success')
      setShowConfirmation(false)
      setTimeout(() => {
        setFormStatus('idle')
        handleRefreshFulfilments()
      }, 3000)
    } catch (error) {
      console.error('Error submitting the review:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit the review')
      setFormStatus('error')
      setShowConfirmation(false)
    }
  }

  const handleCancelConfirmation = () => {
    setShowConfirmation(false)
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">KYC and eligibility checks</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-4">KYC and eligibility checks</h1>

      <div
        role="tablist"
        aria-label="KYC and eligibility checks"
        onKeyDown={onTabKeyDown}
        className="mb-6 flex gap-1 border-b border-gray-200"
      >
        {TABS.map(([value, text]) => (
          <button
            key={value}
            id={`fraud-tab-${value}`}
            type="button"
            role="tab"
            aria-selected={tab === value}
            aria-controls={`fraud-panel-${value}`}
            tabIndex={tab === value ? 0 : -1}
            onClick={() => selectTab(value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === value
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {text}
          </button>
        ))}
      </div>

      <div
        id="fraud-panel-settings"
        role="tabpanel"
        aria-labelledby="fraud-tab-settings"
        hidden={tab !== 'settings'}
      >
        {tab === 'settings' && <FraudSettings />}
      </div>

      <div
        id="fraud-panel-operators"
        role="tabpanel"
        aria-labelledby="fraud-tab-operators"
        hidden={tab !== 'operators'}
      >
        {tab === 'operators' && <FraudOperators />}
      </div>

      <div
        id="fraud-panel-booking"
        role="tabpanel"
        aria-labelledby="fraud-tab-booking"
        hidden={tab !== 'booking'}
      >
        {isFrontApp && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-700">
              Connected to Front conversation. Booking IDs will be automatically detected when available.
            </p>
          </div>
        )}

        <div className="mb-6">
          <label
            htmlFor="bookingId"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Booking ID <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="bookingId"
              type="text"
              value={bookingId}
              onChange={e => setBookingId((e.target as HTMLInputElement).value)}
              className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
              placeholder="Enter Booking ID"
              required
            />
          </div>
          {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
          {bookingId && isFrontApp && (
            <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
          )}
        </div>

        {/* Above the fulfilment table on purpose: the prior defaults are the context for the
          approve/decline decision below, not a footnote to it. */}
        <PreviousDefaultWaiver
          bookingId={bookingId}
          active={tab === 'booking'}
        />

        <div className="mb-6">
          <div className="flex items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-800 mr-2">Fulfilments</h2>
            <button
              type="button"
              onClick={handleRefreshFulfilments}
              className="text-indigo-600 hover:text-indigo-800 focus:outline-none"
              title="Refresh Fulfilments"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>

          {fetchingFulfilments ? (
            <div className="p-4 bg-gray-50 rounded-md flex justify-center items-center">
              <svg
                className="animate-spin h-6 w-6 text-indigo-600 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Loading fulfilments...</span>
            </div>
          ) : (
            <>
              {fulfilments.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-md text-center">
                  {bookingId ? 'No fulfilments found for this booking.' : 'Enter a booking ID to view fulfilments.'}
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Select
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Workflow ID
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {fulfilments.map((fulfilment, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="radio"
                              name="fulfilmentSelection"
                              value={index}
                              checked={selectedFulfilmentIndex === index}
                              onChange={() => handleFulfilmentSelectionChange(index)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{fulfilment.workflowId}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {fulfilment.manualFraudCheckReason || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <form
          onSubmit={handleFormSubmit}
          className="space-y-4"
        >
          {selectedFulfilmentIndex !== -1 && fulfilments[selectedFulfilmentIndex] && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>Selected Fulfilment:</strong> {fulfilments[selectedFulfilmentIndex].workflowId}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action <span className="text-red-500">*</span>
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio text-indigo-600"
                  name="action"
                  value="approve"
                  checked={action === 'approve'}
                  onChange={() => setAction('approve')}
                />
                <span className="ml-2">Approve</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio text-indigo-600"
                  name="action"
                  value="decline"
                  checked={action === 'decline'}
                  onChange={() => setAction('decline')}
                />
                <span className="ml-2">Decline</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              required
            />
          </div>

          {showConfirmation && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800 font-semibold mb-2">
                Are you sure you want to {action === 'approve' ? 'approve' : 'decline'} this review?
              </p>
              <p className="text-xs text-amber-700 mb-3">
                This action will finalise the manual review for the selected fulfilment.
              </p>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'loading' ? 'Processing...' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelConfirmation}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!showConfirmation && (
            <div className="pt-4">
              <button
                type="submit"
                disabled={formStatus === 'loading' || selectedFulfilmentIndex === -1}
                className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? 'Processing...' : 'Finalise Manual Check'}
              </button>
            </div>
          )}
        </form>

        {formStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700">Review finalised successfully!</p>
          </div>
        )}

        {formStatus === 'error' && errorMessage && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="mt-6">
          <button
            type="button"
            onClick={resetForm}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Form
          </button>
        </div>
      </div>
    </div>
  )
}
